import React, { Fragment } from 'react';
import { Route, Switch, RouteComponentProps, Redirect } from 'react-router';
import _ from 'lodash';
import './MainPage.scss';

import { Reports } from 'containers/Reports';
import { Accounts } from 'containers/Accounts';
import { Agencies } from 'containers/Agencies';
import { AppHeader } from 'containers/App/AppHeader';
import { AppMenuBar } from 'containers/App/AppMenuBar';
import { LoadingIndicator } from 'components/LoadingIndicator';

import { MainPageProps, MainPageState } from './MainPageModel';
import { Orders } from 'containers/Orders/Orders';
import { ErrorPage } from '../ErrorPage/ErrorPage';
import MenuBarExpandContext from 'contexts/menuBarExpandContext';
import { Creatives } from 'containers/Creatives/Creatives';
import Swagger from 'containers/Swagger/Swagger';
import { CampaignRedirect } from 'containers/L2Objects/CampaignRedirect';
import { CreativeRedirect } from 'containers/Creatives/CreativeRedirect';
import { Advertisers } from 'containers/Advertisers/Advertisers';
import PermissionContext from 'contexts/permissionContext';
import {
  addOnEnabled,
  hasFuncs,
  hasRoles,
  notRoles,
} from 'core/permission/PermissionDSL';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { Permission } from 'core/auth/Permission';
import { ROUTE_PATH } from 'enum/RoutePath';
import { RoleNames } from 'core';
import CoreContext from 'contexts/coreContext';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { StoredValue } from 'containers/StoredValue/StoredValue';
import { OutboundReportPage } from 'containers/OutboundReportPage';
import { ElementScrollControl } from 'components/ElementScrollControl/ElementScrollControl';
import { RetailMomo } from 'containers/RetailMomo';
import { IntelligentWarning } from 'containers/IntelligentWarning';

export class MainPage extends React.Component<
  RouteComponentProps<{}> & MainPageProps,
  MainPageState
> {
  handler?: number;

  constructor(props: any) {
    super(props);
    this.state = this.props.model.state;
  }

  componentDidMount() {
    this.props.model.init();
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
  }

  componentWillUnmount() {
    this.handler && this.props.model.event.remove(this.handler);
  }

  componentDidUpdate(prevProps, prevState: MainPageState) {
    if (
      prevState.permissionContext.actor !==
      this.props.model.state.permissionContext.actor
    ) {
      this.removeErrorCodeOnActorChange();
    }
  }

  removeErrorCodeOnActorChange() {
    const errorCodeReg = /\/(?:error403|error404)$/;
    if (this.props.location.pathname.match(errorCodeReg)) {
      this.props.history.replace(
        this.props.location.pathname.replace(errorCodeReg, ''),
      );
    }
  }

  renderAccount = (props) => {
    return <Accounts model={this.props.model.accountsModel} />;
  };

  renderOrders = (props) => {
    return <Orders model={this.props.model.ordersModel} />;
  };

  renderCreatives = (props) => {
    return <Creatives {...props} model={this.props.model.creativesModel} />;
  };

  renderReports = (props) => {
    return <Reports {...props} core={this.props.model.core} />;
  };

  renderRetailMomo = (props) => {
    const mainPagePath = this.props.model.mainPagePath;

    return <RetailMomo {...props} mainPagePath={mainPagePath} />;
  };

  renderSwagger = (props) => {
    const authenticationManager =
      this.props.model.core && this.props.model.core.authenticationManager;
    return <Swagger authenticationManager={authenticationManager} />;
  };

  renderAgencies = (props) => {
    return <Agencies model={this.props.model.agenciesModel} />;
  };

  renderCampaignDetail = (props) => {
    const campaignId = props.match.params.campaignId;
    return <CampaignRedirect campaignId={campaignId} />;
  };

  renderCreativeDetail = (props) => {
    const creativeId = props.match.params.creativeId;
    return <CreativeRedirect creativeId={creativeId} />;
  };

  renderAppMenuBar = () => {
    return <AppMenuBar model={this.props.model.barModel} />;
  };

  renderAdvertisers = () => {
    return <Advertisers model={this.props.model.advertisersModel} />;
  };

  renderErrorPage = (errorCode) => {
    const onBackClicked = (path: string) => {
      this.props.history.push(path);
    };
    return (
      <ErrorPage
        errorCode={errorCode}
        onBackClicked={onBackClicked}
        currentPath={this.props.location.pathname}
      />
    );
  };

  renderMainPage = () => {
    const mainPagePath = this.props.model.mainPagePath;
    return <Redirect to={mainPagePath} />;
  };

  renderIntelligentWarning = () => {
    return <IntelligentWarning />;
  };

  renderStoredValue = () => {
    return <StoredValue />;
  };

  renderOutboundReport = () => {
    return <OutboundReportPage />;
  };

  renderMainPageContent = () => {
    const indicator = this.state.loading ? <LoadingIndicator /> : null;
    const model = this.props.model;
    const account = model.account;
    const { sysAdmin, agencyReport, adsReport, agencyAdmin, agencyManager } =
      RoleNames;
    const renderEmpty = () => <div />;

    return (
      <div className='main-page'>
        <AppHeader model={model.headerModel} />
        <div className='main-page-container'>
          {account === null || indicator ? (
            indicator
          ) : (
            <Fragment>
              <Switch>
                <Route
                  path={`*/${ROUTE_PATH.ERROR403}`}
                  render={this.renderAppMenuBar}
                />
                <Route
                  path={`*/${ROUTE_PATH.ERROR404}`}
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns/select-adType'
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns/create'
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns/:campaignId/edit'
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns/:campaignId/copy'
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns'
                  render={renderEmpty}
                />
                <Route path='/creatives/new' render={renderEmpty} />
                <Route
                  path='/creatives/:creativeId/edit'
                  render={renderEmpty}
                />
                <Route render={this.renderAppMenuBar} />
              </Switch>
              <div className='content'>
                <ElementScrollControl>
                  <Switch>
                    <Route
                      path={`*/${ROUTE_PATH.ERROR403}`}
                      component={_.partial(this.renderErrorPage, 403)}
                    />
                    <Route
                      path={`*/${ROUTE_PATH.ERROR404}`}
                      component={_.partial(this.renderErrorPage, 404)}
                    />
                    <Route path='/reports' render={this.renderReports} />
                    <RouteWithPermission
                      path='/index'
                      permissionAware={notRoles(sysAdmin, adsReport)}
                      redirectPath={`/index/${ROUTE_PATH.ERROR403}`}
                      render={this.renderIntelligentWarning}
                    />
                    <RouteWithPermission
                      path='/accounts'
                      permissionAware={hasFuncs(Permission.ACCOUNT_READ)}
                      redirectPath={`/accounts/${ROUTE_PATH.ERROR403}`}
                      render={this.renderAccount}
                    />
                    <RouteWithPermission
                      path='/agencies'
                      permissionAware={hasFuncs(Permission.AGENCY_READ)}
                      redirectPath={`/agencies/${ROUTE_PATH.ERROR403}`}
                      render={this.renderAgencies}
                    />
                    <RouteWithPermission
                      path='/orders'
                      permissionAware={hasFuncs(Permission.ORDER_READ)}
                      redirectPath={`/orders/${ROUTE_PATH.ERROR403}`}
                      render={this.renderOrders}
                    />
                    <RouteWithPermission
                      exact
                      path='/creatives/:creativeId/redirectToDetail'
                      permissionAware={hasFuncs(Permission.CREATIVE_READ)}
                      redirectPath={`/${ROUTE_PATH.ERROR403}`}
                      render={this.renderCreativeDetail}
                    />
                    <RouteWithPermission
                      path='/creatives'
                      permissionAware={hasFuncs(Permission.CREATIVE_READ).and(
                        notRoles(sysAdmin, agencyReport, adsReport),
                      )}
                      redirectPath={`/creatives/${ROUTE_PATH.ERROR403}`}
                      render={this.renderCreatives}
                    />
                    <RouteWithPermission
                      path='/advertisers'
                      permissionAware={hasFuncs(Permission.ADVERTISER_READ)}
                      redirectPath={`/advertisers/${ROUTE_PATH.ERROR403}`}
                      render={this.renderAdvertisers}
                    />
                    <RouteWithPermission
                      path='/campaigns/:campaignId/redirectToDetail'
                      permissionAware={hasFuncs(
                        Permission.ORDER_READ,
                        Permission.CAMPAIGN_READ,
                      )}
                      redirectPath={`/${ROUTE_PATH.ERROR403}`}
                      render={this.renderCampaignDetail}
                    />
                    <RouteWithPermission
                      path='/stored-value'
                      permissionAware={hasRoles(sysAdmin).or(
                        hasRoles(agencyAdmin, agencyManager).and(
                          addOnEnabled(ADDONFEATURE.COMPANY.STORED_VALUE),
                        ),
                      )}
                      redirectPath={`/stored-value/${ROUTE_PATH.ERROR403}`}
                      render={this.renderStoredValue}
                    />
                    <RouteWithPermission
                      path='/outbound-report'
                      permissionAware={hasRoles(sysAdmin)}
                      redirectPath={`/outbound-report/${ROUTE_PATH.ERROR403}`}
                      render={this.renderOutboundReport}
                    />
                    <Route path='/api-swagger' render={this.renderSwagger} />
                    <Route render={this.renderRetailMomo} />
                    <Route render={this.renderMainPage} />
                  </Switch>
                </ElementScrollControl>
              </div>
            </Fragment>
          )}
        </div>
      </div>
    );
  };

  render() {
    const model = this.props.model;
    return (
      <MenuBarExpandContext.Provider
        value={this.props.model.barModel.state.expanded}
      >
        <PermissionContext.Provider value={model.state.permissionContext}>
          <CoreContext.Provider value={model.core}>
            {this.renderMainPageContent()}
          </CoreContext.Provider>
        </PermissionContext.Provider>
      </MenuBarExpandContext.Provider>
    );
  }
}
